import {
    calculateChartRange,
    type MetricAxisSpec,
    type MetricSeriesSpec,
} from '@/components/charts/generic';
import type { BaseChartDataPoint } from '@/domain/behavior';

import { behaviorBand, behaviorPoints } from '../behaviorChartSpec';

/** What a swim speed or tilt chart can show on its right axis, chosen with a radio group. */
export type BehaviorContext = 'temperature' | 'depth' | 'oxygen';

/** A Behavior point that carries the context readings. */
export interface ContextPoint extends BaseChartDataPoint {
    temperature?: number;
    depth?: number;
    oxygen?: number;
}

const UNITS: Record<BehaviorContext, string> = { temperature: '°C', depth: 'm', oxygen: '%' };
const LABEL_KEYS: Record<BehaviorContext, string> = {
    temperature: 'common.temperature',
    depth: 'common.depth',
    oxygen: 'common.oxygen',
};

/** The radio group's value as a context: 'depth', 'oxygen' when there is any, else temperature. */
export function toBehaviorContext(value: string | null, hasOxygen: boolean): BehaviorContext {
    if (value === 'depth') return 'depth';
    if (value === 'oxygen' && hasOxygen) return 'oxygen';
    return 'temperature';
}

/** The bounds of a context axis, over every reading: oxygen without a zero floor. */
function contextRange(context: BehaviorContext, values: (number | undefined)[]) {
    if (context !== 'oxygen') return calculateChartRange(values);
    const readings = values.filter((value) => value != null);
    return readings.length > 0 ? calculateChartRange(readings, 20, false) : { min: 0, max: 100 };
}

/**
 * The chosen context as a band on the right axis, and that axis: its ticks on the left grid
 * lines, its bounds fitted to every reading. The band stays out of the legend; the radio group
 * picks it.
 */
export function contextSeries<TPoint extends ContextPoint>(
    points: TPoint[],
    context: BehaviorContext,
    t: (key: string) => string
): { series: MetricSeriesSpec; axis: MetricAxisSpec } {
    const label = t(LABEL_KEYS[context]);
    const values = points.map((point) => point[context]);
    return {
        series: {
            ...behaviorBand({
                key: `context:${context}`,
                label,
                data: behaviorPoints(points, (point) => point[context]),
            }),
            showInLegend: false,
        },
        axis: {
            label,
            unit: UNITS[context],
            ...contextRange(context, values),
            alignTicksToLeft: true,
        },
    };
}
